/**
 * Damage Claims React Hooks
 * Epic 69: Garancia RMA & Cseregep
 *
 * TanStack Query hooks for damage claim CRUD, approval, replacement.
 */

import {
  approveDamageClaim,
  createDamageClaim,
  getDamageClaim,
  getReplacements,
  issueReplacement,
  listDamageClaims,
  rejectDamageClaim,
  type CreateDamageClaimInput,
  type DamageClaim,
  type DamageClaimStatusType,
  type IssueReplacementInput,
  type RentalReplacement,
} from '@/api/damage-claims';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// QUERIES
// ============================================

export function useDamageClaims(filters?: { status?: DamageClaimStatusType; rentalId?: string }) {
  const query = useQuery({
    queryKey: ['damage-claims', 'list', filters],
    queryFn: async () => {
      const res = await listDamageClaims(filters);
      return res.data;
    },
  });

  return {
    claims: (query.data ?? []) as DamageClaim[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useDamageClaim(id: string | undefined) {
  const query = useQuery({
    queryKey: ['damage-claims', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getDamageClaim(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    claim: query.data as DamageClaim | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useReplacements(rentalId: string | undefined) {
  const query = useQuery({
    queryKey: ['damage-claims', 'replacements', rentalId],
    queryFn: async () => {
      if (!rentalId) throw new Error('No rental ID');
      const res = await getReplacements(rentalId);
      return res.data;
    },
    enabled: !!rentalId,
  });

  return {
    replacements: (query.data ?? []) as RentalReplacement[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useCreateDamageClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDamageClaimInput) => createDamageClaim(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['damage-claims'] });
    },
  });
}

export function useApproveDamageClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveDamageClaim(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['damage-claims'] });
    },
  });
}

export function useRejectDamageClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; rejectionNote: string }) =>
      rejectDamageClaim(data.id, { rejectionNote: data.rejectionNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['damage-claims'] });
    },
  });
}

export function useIssueReplacement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { claimId: string; input: IssueReplacementInput }) =>
      issueReplacement(data.claimId, data.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['damage-claims'] });
    },
  });
}
